import * as Commons from "oc-commons/Commons";
import { v3 } from "./short3";
import * as Maths3 from "./Maths3";
import { Cone } from "./math/Cone";
import * as Scenes from "./Scenes";
import { ConeIntersectionHandler } from "./collision/ConeIntersectionHandler";

import * as _ from "lodash";
import { Line3, Object3D, Plane, Ray, Raycaster, Sphere, Sprite, Vector2, Vector3 } from "three";

declare const STAGE: any;

/**
 * Statische Utility-Klasse zu Mouse und Screen, Umrechnung der Koordinaten und sichtbare Größen,
 * Mouse-Picking (speziell Raycasting für Maus)
 *
 * Eventuell ist ScreenUtils besser geeignet. ScreenUtils ist eine neuere Implementierung
 * und stammt aus dem Easy-Zaunplaner.
 */

/**
 * gibt die aktuell sichtbare Größe einer Kugel in Pixeln zurück
 */
export function visibleSizeInPx(sphere: Sphere): number {
  return visibleSizeInPxCalculator()(sphere.center, sphere.radius);
}

/**
 * Gibt eine Funktion (center, radius = 1) => Pixel-Anzahl zürück
 */
export function visibleSizeInPxCalculator(): (center: Vector3, radius?: number) => number {
  const vsf = visibleSizeFactor();
  return (center: Vector3, radius = 1) => (vsf * radius) / STAGE.camera.position.distanceTo(center);
}

/**
 * gibt das Größenverhältnis Canvas-Höhe zu Camera-Fov zurück
 */
export function visibleSizeFactor(): number {
  return Math.max(STAGE.canvas.height, 1) / (2 * Math.tan((STAGE.camera.fov * Math.PI) / 360));
}

/**
 * ist eine World - Position sichtbar?
 */
export function isVisiblePosition(position: Vector3, min = 0, max = 1): boolean {
  try {
    const screenPosition = position.clone().project(STAGE.camera);
    const x = (screenPosition.x + 1) / 2;
    const y = -(screenPosition.y - 1) / 2;
    return min < x && x < max && min < y && y < max;
  } catch (e) {
    return true;
  }
}

/**
 * gibt die Pixelkoordinaten auf dem Screen eines Punktes im 3D-Raum zurück
 */
export function screenPosition(position: Vector3): Vector3 {
  const screenPosition = position.clone().project(STAGE.camera);
  screenPosition.x = ((screenPosition.x + 1) / 2) * STAGE.canvas.width;
  screenPosition.y = (-(screenPosition.y - 1) / 2) * STAGE.canvas.height;
  return screenPosition;
}

/**
 * gibt die Maus-Koordinaten jeweils als Verhältnis des Canvas zurück, d.h. -1 (links/oben) bis 1 (unten/rechts)
 */
export function getMouseInDeviceCoords(
  event: MouseEvent | { pageX: number; pageY: number },
): Vector3 {
  const canvas = STAGE.canvas;
  const rect = canvas.getBoundingClientRect();

  if (rect.width == 0 || rect.height == 0) {
    return v3();
  }

  const x = event.pageX - rect.left;
  const y = event.pageY - rect.top;

  const mouse = v3();
  mouse.x = (x / rect.width) * 2 - 1;
  mouse.y = -(y / rect.height) * 2 + 1;

  return mouse;
}

/**
 * Rechnet die Mauskoordinaten in Weltkoordinaten um, wobei der z-Wert vorgegeben wird
 */
export function mouseToWorldCoordinates(event: any, z = 100): Vector3 {
  const camera = STAGE.camera;
  const testPoint = camera.position.clone().add(camera.getWorldDirection(v3()).multiplyScalar(z));
  testPoint.project(camera);
  const mouse = getMouseInDeviceCoords(event);
  mouse.z = testPoint.z;
  mouse.unproject(camera);
  return mouse;
}
/**
 * Rechnet die 2DKoordinaten relativ zum Canvas (canvas oben links=0,0)in Weltkoordinaten um, wobei der z-Wert vorgegeben wird
 */
export function v2ToWorldCoordinates(position: Vector2, z = 100): Vector3 {
  const camera = STAGE.camera;
  const testPoint = camera.position.clone().add(camera.getWorldDirection(v3()).multiplyScalar(z));
  testPoint.project(camera);

  const canvas = STAGE.canvas;

  const x = position.x;
  const y = position.y;

  const result = v3();
  result.x = (x / canvas.width) * 2 - 1;
  result.y = -(y / canvas.height) * 2 + 1;
  result.z = testPoint.z;
  result.unproject(camera);
  return result;
}

/**
 * Führt einen Raycast durch, wobei zuerst ein exakter Ray getestet wird, und wenn der nichts findet,
 * mit einem Volumentest (Kegel, der etwa dem Sehbereich unter der Berührposition eines Fingers auf einem
 * Touchdevice entspricht). Dadurch können auch feine Objekte wie Gitter auf einem Touch-Device gut
 * selektiert werden.
 * Über die Eigenschaft intersectionPriority können auch eigentlich durch andere Objekte verdeckte
 * Objekte zurückgegeben werden.
 * @returns das naheste gefundene Objekt mit der höchsten Priorität oder null
 */
export function fetchObjectAtMouse(
  event: any,
  selectableMeshs: Object3D[],
): Object3D | null | undefined {
  const intersection = intersectionAtMouse(event, selectableMeshs);
  return intersection?.object;
}

/**
 * Führt einen Raycast durch, wobei zuerst ein exakter Ray getestet wird, und wenn der nichts findet,
 * mit einem Volumentest (Kegel, der etwa dem Sehbereich unter der Berührposition eines Fingers auf einem
 * Touchdevice entspricht). Dadurch können auch feine Objekte wie Gitter auf einem Touch-Device gut
 * selektiert werden.
 * Über die Eigenschaft intersectionPriority können auch eigentlich durch andere Objekte verdeckte
 * Objekte zurückgegeben werden.
 * @returns die naheste Intersection mit der höchsten Priorität oder null
 * @example
 * //Das Schnitt-Ergebniss liegt in der folgenden Form vor:
 * {
 *      point, // der Schnittpunkt (geht immer durch die Kegel-Achse)
 *      distance, // der Abstand point zum Kegel.origin (die Spitze)
 *      object, // das geschnittene Objekt
 *      face, // das Dreieck
 *      faceIndex, // Index des Dreiecks
 * }
 */
export function intersectionAtMouse(event: any, selectableMeshs: Object3D[]): any {
  if (Commons.isEmpty(selectableMeshs)) {
    return null;
  }
  // probiere einen exakten Treffer auf dem Sehstrahl
  const first = rayCastAtMouse(event, selectableMeshs);
  if (first) {
    return first;
  }
  const coneTestPixel = STAGE.settings.raycaster.coneTestPixel;
  if (coneTestPixel == 0) {
    return null;
  }
  //Fallback für Cone Test
  const mouse = getMouseInDeviceCoords(event);
  const cone = new Cone().setFromCameraForTouch(mouse as any as Vector2, coneTestPixel);
  let minPriority = 0;
  let minIntersection: any = {
    distance: Number.POSITIVE_INFINITY,
  };
  new ConeIntersectionHandler(selectableMeshs, { cone }).intersect((intersection: any) => {
    const priority = getIntersectionPriorityUp(intersection.object);
    if (
      priority > minPriority ||
      (priority == minPriority && minIntersection.distance > intersection.distance)
    ) {
      minIntersection = intersection;
      minPriority = priority;
    }
  });
  if (!Number.isFinite(minIntersection.distance)) {
    return null;
  }

  return minIntersection;
}

/**
 * führt einen normalen RayCast durch, der aber nicht besonders für Touch-Devices geeignet ist,
 * da lieber fetchObjectAtMouse nehmen.
 */
export function rayCastAtMouse(
  event: any,
  selectableMeshs: Object3D[],
  returnAllMatches = false,
): any {
  if (Commons.isEmpty(selectableMeshs)) {
    return undefined;
  }
  const mouse = getMouseInDeviceCoords(event);

  const raycaster = new Raycaster();
  raycaster.setFromCamera(mouse as any as Vector2, STAGE.camera);
  const intersects: any[] = raycaster.intersectObjects(selectableMeshs, true);

  intersects.sort((a, b) => {
    const aPrio = getIntersectionPriorityUp(a.object);
    const bPrio = getIntersectionPriorityUp(b.object);
    const n = bPrio - aPrio;
    return n !== 0 ? n : a.distance - b.distance;
  });

  if (returnAllMatches) {
    return intersects;
  }

  // const first = intersects.find(o=>!(o.object instanceof THREE.Sprite));
  const first = _.first(intersects);

  if (first == null) {
    return null;
  }

  first.ray = raycaster.ray;

  return first;
}

/**
 * Gibt den nächsten Punkt auf dem Strahl durch die Maus zurück zu einer gegebenen Plane
 */
export function rayCastAtMouseOnPlane(
  event: any,
  plane: Plane,
  offset = Maths3.vector.zero,
): Vector3 | null {
  const ray = getRayFromMouse(event);
  ray.origin.sub(offset);
  const rayMeetsPlane = ray.intersectPlane(plane, v3());
  return rayMeetsPlane;
}

/**
 * Gibt den nächsten Punkt auf dem Strahl durch die Maus zurück zu einer gegebenen Linie
 */
export function rayCastAtMouseOnLine(
  event: any,
  line: Line3,
  offset = Maths3.vector.zero,
): Vector3[] {
  const ray = getRayFromMouse(event);
  ray.origin.sub(offset);
  const pointOnMouseRay = v3();
  const pointOnRay = v3();
  ray.distanceSqToSegment(line.start, line.end, pointOnMouseRay, pointOnRay);
  return [pointOnRay, pointOnMouseRay];
}

/**
 * Gibt den nächsten Punkt auf dem Strahl durch die Maus zurück zu einem gegebenen Punkt
 */
export function rayCastAtMouseToPoint(
  event: any,
  point: Vector3,
  offset = Maths3.vector.zero,
): Vector3 {
  const ray = getRayFromMouse(event);
  ray.origin.sub(offset);
  const position = ray.closestPointToPoint(point, v3());
  position.add(offset);
  return position;
}

/**
 * @returns {*|Debugs.ray}
 */
export function getRayFromMouse(event: any): Ray {
  const raycaster = new Raycaster();
  const mouse = getMouseInDeviceCoords(event);
  raycaster.setFromCamera(mouse as any as Vector2, STAGE.camera);
  return raycaster.ray;
}

/**
 * gibt die intersectionPriority des Objektes bzw. des ersten Parents mit intersectionPriority zurück, oder 0
 * oder -1 wenn object nicht visible
 */
export function getIntersectionPriorityUp(object: Object3D): number {
  const intersectionPriorityHolder: any = Scenes.findUp(
    object,
    (o: any) => o._intersectionPriority != null,
  );
  return intersectionPriorityHolder ? intersectionPriorityHolder.intersectionPriority : 0;
}

/**
 * Schickt eine Reihe von Rays in die Szene, entsprechend dem gerasterten Kugelschatten des Objektes (Bounding Sphere),
 * und gibt die Schnittpunkte mit der Szene (oder der Grund-Plane) zurück.
 */
export function rayCastAtMouseCircleRaster(
  event: any,
  selectableMeshs: Object3D[],
  radiusInWorld: number,
  numberOfRays = 10,
  plane: Plane,
) {
  const mouse = getMouseInDeviceCoords(event);
  const raycaster = new Raycaster();
  raycaster.setFromCamera(mouse as any as Vector2, STAGE.camera);
  return rayCastCircleRaster(raycaster.ray, selectableMeshs, radiusInWorld, numberOfRays, plane);
}

/**
 * Schickt eine Reihe von Rays in die Szene, entsprechend dem gerasterten Kugelschatten des Objektes (Bounding Sphere),
 * und gibt die Schnittpunkte mit der Szene (oder der Grund-Plane) zurück.
 */
export function rayCastCircleRaster(
  ray: Ray,
  selectableMeshs: Object3D[],
  radiusInWorld: number,
  numberOfRays = 10,
  plane: Plane,
) {
  const raycaster = new Raycaster(ray.origin, ray.direction);

  const result: { ray: Ray; points: Vector3[] } = {
    ray,
    points: [],
  };

  Scenes.iteratePointsOnCircleRaster(ray, radiusInWorld, numberOfRays, (rasterPoint: Vector3) => {
    raycaster.ray.origin = rasterPoint;
    const intersects = raycaster.intersectObjects(selectableMeshs, true);
    const first = intersects.find((o) => !(o.object instanceof Sprite));
    if (first) {
      result.points.push(first.point);
      //Utils.debugPoint(first.point)
    } else if (plane) {
      const planePoint = raycaster.ray.intersectPlane(plane, v3());
      if (planePoint) {
        result.points.push(planePoint);
      }
    }
  });
  return result;
}
